import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Fab,
  IconButton,
  Paper,
  Typography,
  CircularProgress,
  LinearProgress,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  Checkbox,
} from '@mui/material';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../hooks/useAuth';
import api from '../../api/config';
import { QUICK_NOTES } from '../../api/endpoints';
import { extractList, extractPagination } from '../../utils/response';
import { SweetAlert } from '../../utils/sweetAlert';
import { toast } from 'react-toastify';

const SAVE_SUCCESS_MS = 2200;
const PER_PAGE = 30;
const SAVE_DEBOUNCE_MS = 900;
const SCROLL_LOAD_THRESHOLD = 72;
const MIN_W = 300;
const MIN_H = 260;
const MAX_W = 520;
const MAX_H = 640;
const LS_SIZE = 'quickNotes.panelSize';
const LS_POS = 'quickNotes.panelPosition';
const LS_FAB_POS = 'quickNotes.fabPosition';
const DEFAULT_SIZE = { width: 380, height: 520 };
const EDGE_HIT = 8;
const CORNER_HIT = 12;
const VIEW_MARGIN = 8;
/** Top resize strip starts just under the drag header so it does not fight header buttons */
const RESIZE_TOP = 44;

const pickCardTimestampRaw = (row) => {
  if (!row || typeof row !== 'object') return null;
  const a = row.attributes && typeof row.attributes === 'object' ? row.attributes : null;
  return row.updated_at ?? row.created_at ?? a?.updated_at ?? a?.created_at ?? null;
};

const formatCardDate = (row) => {
  const v = pickCardTimestampRaw(row);
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatCardTime = (row) => {
  const v = pickCardTimestampRaw(row);
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightPreviewSpans = (text, query) => {
  const q = query.trim();
  if (!q) return [{ key: '0', text, mark: false }];
  const parts = text.split(new RegExp(`(${escapeRegExp(q)})`, 'gi'));
  return parts.map((p, i) => ({
    key: String(i),
    text: p,
    mark: p.toLowerCase() === q.toLowerCase(),
  }));
};

const pickNoteText = (obj) => {
  if (!obj || typeof obj !== 'object') return '';
  const attrs = obj.attributes && typeof obj.attributes === 'object' ? obj.attributes : null;
  const v =
    obj.note ??
    obj.content ??
    attrs?.note ??
    attrs?.content ??
    obj.body ??
    obj.text ??
    obj.description ??
    '';
  return typeof v === 'string' ? v : String(v ?? '');
};

const looksLikeHtml = (s) => typeof s === 'string' && /<[a-z][\s\S]*>/i.test(s);

/** Prefer explicit HTML fields; else use `note` when it contains markup. */
const pickNoteHtml = (obj) => {
  if (!obj || typeof obj !== 'object') return '';
  const attrs = obj.attributes && typeof obj.attributes === 'object' ? obj.attributes : null;
  const h =
    obj.note_html ??
    attrs?.note_html ??
    obj.html ??
    attrs?.html ??
    null;
  if (typeof h === 'string' && h.trim()) return h.trim();
  const t = pickNoteText(obj);
  return looksLikeHtml(t) ? t.trim() : '';
};

/** Whether the API row represents a pinned note (common field names). */
const isPinned = (row) => {
  if (!row || typeof row !== 'object') return false;
  const a = row.attributes;
  if (a && typeof a === 'object') {
    const p = a.is_pinned ?? a.pinned ?? a.is_pin ?? a.pin;
    if (p !== undefined && p !== null) return Boolean(p);
  }
  const top = row.is_pinned ?? row.pinned ?? row.is_pin ?? row.pin;
  if (top !== undefined && top !== null) return Boolean(top);
  const at = row.pinned_at;
  return at != null && String(at).trim() !== '';
};

const isTodoComplete = (row) => {
  if (!row || typeof row !== 'object') return false;
  const a = row.attributes;
  if (a && typeof a === 'object') {
    const c = a.is_completed ?? a.completed ?? a.is_complete ?? a.done;
    if (c !== undefined && c !== null) return Boolean(c);
  }
  const top = row.is_completed ?? row.completed ?? row.is_complete ?? row.done;
  if (top !== undefined && top !== null) return Boolean(top);
  const at = row.completed_at;
  return at != null && String(at).trim() !== '';
};

const previewLine = (row) => {
  const raw = pickNoteText(row);
  const t = raw.replace(/\s+/g, ' ').trim();
  if (!t) return '—';
  return t.length > 140 ? `${t.slice(0, 140)}…` : t;
};

/** Windows Sticky Notes–style palette */
const sticky = {
  headerBg: '#fdd835',
  headerIcon: 'rgba(0,0,0,0.72)',
  bodyBg: '#2d2d2d',
  bodyText: '#e6e6e6',
  placeholder: 'rgba(255,255,255,0.38)',
  footerBg: '#252525',
  footerBorder: '#1a1a1a',
  fabBg: '#fdd835',
  fabColor: '#1a1a1a',
};

/** Todo list view (reference UI) */
const todoUi = {
  pageBg: '#141414',
  inputBg: '#ffffff',
  inputText: '#111111',
  addBtn: '#fdd835',
  addBtnText: '#0d0d0d',
  checkColor: '#fdd835',
  textDone: 'rgba(255,255,255,0.42)',
};

/** Scroll list: slim dark thumb/track (webkit + Firefox) */
const quickNotesListScrollSx = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  px: 1.5,
  pb: 1.5,
  scrollbarGutter: 'stable',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255,255,255,0.28) rgba(255,255,255,0.04)',
  '&::-webkit-scrollbar': { width: 7 },
  '&::-webkit-scrollbar-track': {
    marginBlock: 4,
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 8,
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255,255,255,0.22)',
    borderRadius: 8,
    border: '2px solid transparent',
    backgroundClip: 'padding-box',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(255,255,255,0.34)',
    border: '2px solid transparent',
    backgroundClip: 'padding-box',
  },
};

const readStoredSize = () => {
  try {
    const raw = localStorage.getItem(LS_SIZE);
    if (!raw) return DEFAULT_SIZE;
    const o = JSON.parse(raw);
    const w = Number(o?.width);
    const h = Number(o?.height);
    if (!Number.isFinite(w) || !Number.isFinite(h)) return DEFAULT_SIZE;
    return {
      width: Math.min(MAX_W, Math.max(MIN_W, w)),
      height: Math.min(MAX_H, Math.max(MIN_H, h)),
    };
  } catch {
    return DEFAULT_SIZE;
  }
};

const defaultFabPosition = () => {
  if (typeof window === 'undefined') return { right: 88, bottom: 24 };
  return { right: 88, bottom: 24 };
};

const readStoredFabPosition = () => {
  try {
    const raw = localStorage.getItem(LS_FAB_POS);
    if (!raw) return null;
    const o = JSON.parse(raw);
    const right = Number(o?.right);
    const bottom = Number(o?.bottom);
    if (!Number.isFinite(right) || !Number.isFinite(bottom)) return null;
    return { right, bottom };
  } catch {
    return null;
  }
};

const readStoredPosition = () => {
  try {
    const raw = localStorage.getItem(LS_POS);
    if (!raw) return null;
    const o = JSON.parse(raw);
    const left = Number(o?.left);
    const top = Number(o?.top);
    if (!Number.isFinite(left) || !Number.isFinite(top)) return null;
    return { left, top };
  } catch {
    return null;
  }
};

const defaultPanelPosition = () => {
  const sz = readStoredSize();
  if (typeof window === 'undefined') return { left: 88, top: 120 };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = vw >= 600 ? 88 : 12;
  const top = Math.max(VIEW_MARGIN, vh - sz.height - 24);
  return { left, top };
};

const clampPanel = (left, top, width, height) => {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const m = VIEW_MARGIN;
  let w = Math.min(MAX_W, Math.max(MIN_W, width));
  let h = Math.min(MAX_H, Math.max(MIN_H, height));
  w = Math.min(w, vw - 2 * m);
  h = Math.min(h, vh - 2 * m);
  const l = Math.max(m, Math.min(left, vw - w - m));
  const t = Math.max(m, Math.min(top, vh - h - m));
  return { left: l, top: t, width: w, height: h };
};

const applyResizeByEdge = (edge, dx, dy, L0, T0, W0, H0) => {
  let L = L0;
  let T = T0;
  let W = W0;
  let H = H0;
  const clampW = (w) => Math.min(MAX_W, Math.max(MIN_W, w));
  const clampH = (h) => Math.min(MAX_H, Math.max(MIN_H, h));
  switch (edge) {
    case 'e':
      W = clampW(W0 + dx);
      break;
    case 'w': {
      const nw = clampW(W0 - dx);
      L = L0 + W0 - nw;
      W = nw;
      break;
    }
    case 's':
      H = clampH(H0 + dy);
      break;
    case 'n': {
      const nh = clampH(H0 - dy);
      T = T0 + H0 - nh;
      H = nh;
      break;
    }
    case 'se':
      W = clampW(W0 + dx);
      H = clampH(H0 + dy);
      break;
    case 'sw': {
      const nw = clampW(W0 - dx);
      L = L0 + W0 - nw;
      W = nw;
      H = clampH(H0 + dy);
      break;
    }
    case 'ne':
      W = clampW(W0 + dx);
      {
        const nh = clampH(H0 - dy);
        T = T0 + H0 - nh;
        H = nh;
      }
      break;
    case 'nw': {
      const nw = clampW(W0 - dx);
      L = L0 + W0 - nw;
      W = nw;
      const nh = clampH(H0 - dy);
      T = T0 + H0 - nh;
      H = nh;
      break;
    }
    default:
      break;
  }
  return clampPanel(L, T, W, H);
};

const formDataConfig = {
  transformRequest: [
    (body, headers) => {
      if (body instanceof FormData) {
        delete headers['Content-Type'];
      }
      return body;
    },
  ],
};

const exec = (command, value = false) => {
  try {
    document.execCommand(command, false, value);
  } catch {
    /* ignore */
  }
};

const QuickNotesDock = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [panelSize, setPanelSize] = useState(readStoredSize);
  const [panelPosition, setPanelPosition] = useState(() => readStoredPosition() ?? defaultPanelPosition());
  const [noteType, setNoteType] = useState('note');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [nextPage, setNextPage] = useState(2);
  const [noMoreOlder, setNoMoreOlder] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveShowSuccess, setSaveShowSuccess] = useState(false);
  const saveSuccessTimerRef = useRef(null);
  const [activeNoteId, setActiveNoteId] = useState(null);
  /** list = all notes + search; edit = single pad (autosave, text stays) */
  const [screen, setScreen] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [openingNoteId, setOpeningNoteId] = useState(null);
  const [noteActionBusyId, setNoteActionBusyId] = useState(null);
  const [todoNewInput, setTodoNewInput] = useState('');
  const [todoQuickAddBusy, setTodoQuickAddBusy] = useState(false);
  /** Bumps when we need to push text into the contenteditable (not mounted on list screen). */
  const [editorSessionKey, setEditorSessionKey] = useState(0);
  const editorBootstrapRef = useRef({ text: '', html: null });
  /** Maps `<img src>` value (data URL from picker, or legacy blob URL) → File for multipart `attachments[n]`. */
  const blobUrlToFileRef = useRef(new Map());
  const saveTimerRef = useRef(null);
  const historyScrollRef = useRef(null);
  const editorRef = useRef(null);
  const [fabPosition, setFabPosition] = useState(() => readStoredFabPosition() ?? defaultFabPosition());
  const fabDragRef = useRef({ dragging: false, startX: 0, startY: 0, startRight: 0, startBottom: 0 });
  const fileInputRef = useRef(null);
  const panelSizeRef = useRef(panelSize);
  const panelPositionRef = useRef(panelPosition);
  const interactRef = useRef({
    mode: 'idle',
    edge: null,
    startX: 0,
    startY: 0,
    L0: 0,
    T0: 0,
    W0: 0,
    H0: 0,
  });
  const skipPublicPaths = location.pathname === '/login' || location.pathname === '/register';

  panelSizeRef.current = panelSize;
  panelPositionRef.current = panelPosition;

  useEffect(() => {
    localStorage.setItem(LS_SIZE, JSON.stringify(panelSize));
  }, [panelSize]);

  useEffect(() => {
    localStorage.setItem(LS_POS, JSON.stringify(panelPosition));
  }, [panelPosition]);

  useEffect(() => {
    localStorage.setItem(LS_FAB_POS, JSON.stringify(fabPosition));
  }, [fabPosition]);

  useEffect(() => {
    const clampIntoView = () => {
      const w = panelSizeRef.current.width;
      const h = panelSizeRef.current.height;
      const { left, top } = panelPositionRef.current;
      const o = clampPanel(left, top, w, h);
      setPanelPosition({ left: o.left, top: o.top });
      setPanelSize({ width: o.width, height: o.height });
    };
    window.addEventListener('resize', clampIntoView);
    return () => window.removeEventListener('resize', clampIntoView);
  }, []);

  useEffect(() => {
    if (!open) setSearchQuery('');
  }, [open]);

  const itemsNewestFirst = useMemo(() => [...items].reverse(), [items]);

  const filteredNotes = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let base = itemsNewestFirst;
    if (q) {
      base = itemsNewestFirst.filter((row) => {
        const preview = previewLine(row).toLowerCase();
        const raw = pickNoteText(row).toLowerCase();
        return preview.includes(q) || raw.includes(q);
      });
    }
    return [...base].sort((a, b) => {
      const pa = isPinned(a) ? 1 : 0;
      const pb = isPinned(b) ? 1 : 0;
      if (pb !== pa) return pb - pa;
      return 0;
    });
  }, [itemsNewestFirst, searchQuery]);

  const activeListRow = useMemo(
    () => items.find((r) => r.id === activeNoteId) ?? (activeNoteId ? { id: activeNoteId } : null),
    [items, activeNoteId]
  );

  const hasMoreOlder =
    !noMoreOlder &&
    items.length > 0 &&
    (total > items.length || (total === 0 && items.length >= PER_PAGE));

  const getEditorPlain = () => (editorRef.current?.innerText || '').trim();

  const revokeAllEditorBlobUrls = useCallback(() => {
    blobUrlToFileRef.current.forEach((_file, url) => {
      if (typeof url === 'string' && url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* ignore */
        }
      }
    });
    blobUrlToFileRef.current.clear();
  }, []);

  const hasEditorSaveableContent = useCallback(() => {
    if (noteType === 'todo') {
      return getEditorPlain().length > 0;
    }
    const plain = getEditorPlain();
    const html = editorRef.current?.innerHTML || '';
    if (plain.trim().length > 0) return true;
    if (/<img\s/i.test(html)) return true;
    const stripped = html.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim();
    return stripped.length > 0;
  }, [noteType]);

  const fetchPage = useCallback(
    async (page) => {
      const { data } = await api.get(QUICK_NOTES.LIST, {
        params: {
          page,
          per_page: PER_PAGE,
          paginate: 1,
          sort_by: 'created_at',
          sort_direction: 'desc',
          'relations[0]': 'author',
          'filters[type]': noteType,
          scope: 'self',
        },
      });
      const list = extractList(data);
      const arr = Array.isArray(list) ? list : [];
      const pag = extractPagination(data);
      const t = pag.total ?? data?.data?.total ?? data?.total ?? 0;
      return { rows: arr, total: typeof t === 'number' ? t : parseInt(String(t), 10) || 0 };
    },
    [noteType]
  );

  const loadInitial = useCallback(async () => {
    setLoadingInitial(true);
    setNoMoreOlder(false);
    try {
      const { rows, total: tot } = await fetchPage(1);
      const chronological = [...rows].reverse();
      setItems(chronological);
      setTotal(tot);
      setNextPage(2);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load quick notes');
      setItems([]);
      setTotal(0);
    } finally {
      setLoadingInitial(false);
      requestAnimationFrame(() => {
        const el = historyScrollRef.current;
        if (el) el.scrollTop = 0;
      });
    }
  }, [fetchPage]);

  /** Refetch first page without full-panel loading spinner (e.g. after todo complete toggle). */
  const reloadFirstPageQuiet = useCallback(async () => {
    try {
      const { rows, total: tot } = await fetchPage(1);
      const chronological = [...rows].reverse();
      setItems(chronological);
      setTotal(tot);
      setNextPage(2);
      setNoMoreOlder(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not refresh list');
    }
  }, [fetchPage]);

  useEffect(() => {
    if (!open) return;
    setActiveNoteId(null);
    setScreen('list');
    setSaveShowSuccess(false);
    if (saveSuccessTimerRef.current) {
      clearTimeout(saveSuccessTimerRef.current);
      saveSuccessTimerRef.current = null;
    }
    setTodoNewInput('');
    if (editorRef.current) {
      revokeAllEditorBlobUrls();
      editorRef.current.innerHTML = '';
    }
    loadInitial();
  }, [open, noteType, loadInitial, revokeAllEditorBlobUrls]);

  useEffect(() => {
    if (!open || screen !== 'edit') return;
    requestAnimationFrame(() => editorRef.current?.focus());
  }, [open, screen]);

  useLayoutEffect(() => {
    if (screen !== 'edit' || !editorRef.current) return;
    const { text = '', html } = editorBootstrapRef.current;
    const el = editorRef.current;
    el.innerHTML = '';
    const rich = typeof html === 'string' && html.trim() && looksLikeHtml(html);
    if (rich) {
      el.innerHTML = html.trim();
    } else {
      el.innerText = text ?? '';
    }
  }, [screen, editorSessionKey]);

  const loadOlder = useCallback(async () => {
    if (!hasMoreOlder || loadingOlder || loadingInitial) return;
    const el = historyScrollRef.current;
    if (!el) return;
    const prevScrollHeight = el.scrollHeight;
    const prevScrollTop = el.scrollTop;
    setLoadingOlder(true);
    try {
      const { rows, total: tot } = await fetchPage(nextPage);
      if (!rows.length) {
        setNoMoreOlder(true);
        if (Number.isFinite(tot) && tot > 0) setTotal(tot);
        return;
      }
      const olderChrono = [...rows].reverse();
      setItems((prev) => [...olderChrono, ...prev]);
      setTotal(tot);
      setNextPage((p) => p + 1);
      requestAnimationFrame(() => {
        const box = historyScrollRef.current;
        if (!box) return;
        const d = box.scrollHeight - prevScrollHeight;
        box.scrollTop = prevScrollTop + d;
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load older notes');
    } finally {
      setLoadingOlder(false);
    }
  }, [fetchPage, nextPage, hasMoreOlder, loadingOlder, loadingInitial]);

  const flushSave = useCallback(async () => {
    if (!hasEditorSaveableContent()) return true;
    if (saving) return false;
    let didSaveOk = false;
    setSaving(true);
    setSaveShowSuccess(false);
    try {
      const fd = new FormData();
      fd.append('type', noteType);

      if (noteType === 'note') {
        const htmlContent = (editorRef.current?.innerHTML || '').trim();
        fd.append('note', htmlContent || getEditorPlain());
        const holder = document.createElement('div');
        holder.innerHTML = htmlContent;
        const seenSrc = new Set();
        let att = 0;
        holder.querySelectorAll('img').forEach((img) => {
          const src = img.getAttribute('src');
          if (!src || seenSrc.has(src)) return;
          const file = blobUrlToFileRef.current.get(src);
          if (file) {
            seenSrc.add(src);
            fd.append(`attachments[${att}]`, file, file.name || `image-${att}.png`);
            att += 1;
          }
        });
      } else {
        fd.append('note', getEditorPlain());
      }

      const applyServerHtmlToEditor = (rowLike) => {
        if (noteType !== 'note' || !editorRef.current || !rowLike || typeof rowLike !== 'object') return;
        const h =
          pickNoteHtml(rowLike) ||
          (typeof rowLike.note === 'string' && looksLikeHtml(rowLike.note) ? rowLike.note : '') ||
          '';
        if (!h.trim()) return;
        editorRef.current.querySelectorAll('img').forEach((img) => {
          const src = img.getAttribute('src') || '';
          if (src.startsWith('blob:')) {
            try {
              URL.revokeObjectURL(src);
            } catch {
              /* ignore */
            }
          }
          blobUrlToFileRef.current.delete(src);
        });
        editorRef.current.innerHTML = h.trim();
      };

      if (activeNoteId) {
        fd.append('_method', 'PUT');
        const { data } = await api.post(QUICK_NOTES.BY_ID(activeNoteId), fd, formDataConfig);
        const updated = data?.data ?? data;
        setItems((prev) =>
          prev.map((row) =>
            row.id === activeNoteId ? { ...row, ...(typeof updated === 'object' && updated ? updated : {}) } : row
          )
        );
        applyServerHtmlToEditor(updated);
        didSaveOk = true;
        return true;
      }

      const { data } = await api.post(QUICK_NOTES.LIST, fd, formDataConfig);
      const created = data?.data ?? data;
      if (created && typeof created === 'object' && created.id != null) {
        applyServerHtmlToEditor(created);
        setActiveNoteId(created.id);
        setItems((prev) => {
          if (prev.some((r) => r.id === created.id)) {
            return prev.map((r) => (r.id === created.id ? { ...r, ...created } : r));
          }
          return [...prev, { ...created }];
        });
        setTotal((t) => (Number.isFinite(t) ? t + 1 : 1));
        requestAnimationFrame(() => {
          const el = historyScrollRef.current;
          if (el) el.scrollTop = 0;
        });
        didSaveOk = true;
        return true;
      }
      loadInitial();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save note');
      return false;
    } finally {
      setSaving(false);
      if (didSaveOk) {
        setSaveShowSuccess(true);
        if (saveSuccessTimerRef.current) {
          clearTimeout(saveSuccessTimerRef.current);
        }
        saveSuccessTimerRef.current = setTimeout(() => {
          saveSuccessTimerRef.current = null;
          setSaveShowSuccess(false);
        }, SAVE_SUCCESS_MS);
      }
    }
  }, [noteType, saving, loadInitial, activeNoteId, hasEditorSaveableContent]);

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      flushSave();
    }, SAVE_DEBOUNCE_MS);
  }, [flushSave]);

  useEffect(
    () => () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (saveSuccessTimerRef.current) {
        clearTimeout(saveSuccessTimerRef.current);
        saveSuccessTimerRef.current = null;
      }
    },
    []
  );

  const onHistoryScroll = useCallback(() => {
    const el = historyScrollRef.current;
    if (!el || loadingOlder || loadingInitial || !hasMoreOlder) return;
    if (el.scrollTop <= SCROLL_LOAD_THRESHOLD) {
      loadOlder();
    }
  }, [loadOlder, loadingOlder, loadingInitial, hasMoreOlder]);

  const onEditorInput = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (hasEditorSaveableContent()) scheduleSave();
  };

  const onEditorBlur = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (hasEditorSaveableContent()) flushSave();
  };

  const focusEditor = () => {
    requestAnimationFrame(() => editorRef.current?.focus());
  };

  const applyFormat = (command, value = false) => {
    editorRef.current?.focus();
    exec(command, value);
  };

  const onPickImage = (e) => {
    if (noteType !== 'note') return;
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== 'string') return;
      blobUrlToFileRef.current.set(dataUrl, file);
      editorRef.current?.focus();
      exec('insertImage', dataUrl);
      scheduleSave();
    };
    reader.readAsDataURL(file);
  };

  const handleNewNote = async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    let ok = true;
    if (screen === 'edit' && hasEditorSaveableContent()) {
      ok = await flushSave();
    }
    editorBootstrapRef.current = { text: '', html: null };
    setEditorSessionKey((k) => k + 1);
    setActiveNoteId(null);
    if (ok && editorRef.current) {
      revokeAllEditorBlobUrls();
      editorRef.current.innerHTML = '';
    }
    setScreen('edit');
    focusEditor();
  };

  const handleBackToList = async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (hasEditorSaveableContent()) {
      await flushSave();
    }
    setActiveNoteId(null);
    revokeAllEditorBlobUrls();
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
    setScreen('list');
    requestAnimationFrame(() => {
      const el = historyScrollRef.current;
      if (el) el.scrollTop = 0;
    });
  };

  const handleClose = async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (screen === 'edit' && hasEditorSaveableContent()) {
      await flushSave();
    }
    setSaveShowSuccess(false);
    if (saveSuccessTimerRef.current) {
      clearTimeout(saveSuccessTimerRef.current);
      saveSuccessTimerRef.current = null;
    }
    setActiveNoteId(null);
    revokeAllEditorBlobUrls();
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
    setScreen('list');
    setOpen(false);
  };

  const openNoteForEdit = async (row) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const targetId = row.id ?? null;
    if (targetId == null) return;

    if (targetId !== activeNoteId) {
      revokeAllEditorBlobUrls();
    }

    if (screen === 'edit' && hasEditorSaveableContent() && activeNoteId !== targetId) {
      await flushSave();
    }

    let text = pickNoteText(row);
    let html = pickNoteHtml(row) || null;
    setOpeningNoteId(targetId);
    try {
      const { data } = await api.get(QUICK_NOTES.BY_ID(targetId));
      const one = data?.data?.data ?? data?.data ?? data;
      if (one && typeof one === 'object') {
        text = pickNoteText(one);
        html = pickNoteHtml(one) || (looksLikeHtml(text) ? text : null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load note');
    } finally {
      setOpeningNoteId(null);
    }

    editorBootstrapRef.current = {
      text,
      html: html && looksLikeHtml(html) ? html : null,
    };
    setEditorSessionKey((k) => k + 1);
    setActiveNoteId(targetId);
    setScreen('edit');
  };

  const handleTogglePin = useCallback(async (row, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    const id = row?.id;
    if (id == null) return;
    setNoteActionBusyId(id);
    try {
      const pinned = isPinned(row);
      const { data } = pinned
        ? await api.get(QUICK_NOTES.UNMARK_AS_PIN(id))
        : await api.post(QUICK_NOTES.MARK_AS_PIN(id));
      const updated = data?.data?.data ?? data?.data ?? data;
      setItems((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          if (updated && typeof updated === 'object' && !Array.isArray(updated)) {
            return { ...r, ...updated };
          }
          return { ...r, is_pinned: !isPinned(r) };
        })
      );
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update pin');
    } finally {
      setNoteActionBusyId(null);
    }
  }, []);

  const handleQuickAddTodo = useCallback(async () => {
    const text = todoNewInput.trim();
    if (!text || todoQuickAddBusy) return;
    setTodoQuickAddBusy(true);
    try {
      const fd = new FormData();
      fd.append('type', 'todo');
      fd.append('note', text);
      const { data } = await api.post(QUICK_NOTES.LIST, fd, formDataConfig);
      const created = data?.data ?? data;
      if (created && typeof created === 'object' && created.id != null) {
        setTodoNewInput('');
        setItems((prev) => {
          if (prev.some((r) => r.id === created.id)) {
            return prev.map((r) => (r.id === created.id ? { ...r, ...created, note: text } : r));
          }
          return [...prev, { ...created, note: text }];
        });
        setTotal((t) => (Number.isFinite(t) ? t + 1 : 1));
        requestAnimationFrame(() => {
          const el = historyScrollRef.current;
          if (el) el.scrollTop = 0;
        });
      } else {
        setTodoNewInput('');
        loadInitial();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not add todo');
    } finally {
      setTodoQuickAddBusy(false);
    }
  }, [todoNewInput, todoQuickAddBusy, loadInitial]);

  const handleToggleTodoComplete = useCallback(
    async (row) => {
      const id = row?.id;
      if (id == null) return;
      const done = isTodoComplete(row);
      setNoteActionBusyId(id);
      try {
        await (done
          ? api.get(QUICK_NOTES.UNMARK_AS_COMPLETE(id))
          : api.get(QUICK_NOTES.MARK_AS_COMPLETE(id)));
        await reloadFirstPageQuiet();
      } catch (err) {
        toast.error(err.response?.data?.message || 'Could not update todo');
      } finally {
        setNoteActionBusyId(null);
      }
    },
    [reloadFirstPageQuiet]
  );

  const handleDeleteNote = useCallback(
    async (row, e) => {
      e?.stopPropagation?.();
      e?.preventDefault?.();
      const id = row?.id;
      if (id == null) return;
      const result = await SweetAlert.confirm({
        title: noteType === 'todo' ? 'Delete todo?' : 'Delete note?',
        text: 'This action cannot be undone.',
        confirmButtonText: 'Delete',
        icon: 'warning',
      });
      if (!result.isConfirmed) return;
      setNoteActionBusyId(id);
      try {
        await api.delete(QUICK_NOTES.BY_ID(id));
        setItems((prev) => prev.filter((r) => r.id !== id));
        setTotal((t) => (Number.isFinite(t) ? Math.max(0, t - 1) : 0));
        if (activeNoteId === id) {
          if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
          }
          setActiveNoteId(null);
          editorBootstrapRef.current = { text: '', html: null };
          setEditorSessionKey((k) => k + 1);
          revokeAllEditorBlobUrls();
          if (editorRef.current) {
            editorRef.current.innerHTML = '';
          }
          setScreen('list');
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Could not delete note');
      } finally {
        setNoteActionBusyId(null);
      }
    },
    [activeNoteId, noteType, revokeAllEditorBlobUrls]
  );

  const startPanelInteraction = useCallback((mode, edge, clientX, clientY) => {
    const ir = interactRef.current;
    const pos = panelPositionRef.current;
    const sz = panelSizeRef.current;
    ir.mode = mode;
    ir.edge = edge;
    ir.startX = clientX;
    ir.startY = clientY;
    ir.L0 = pos.left;
    ir.T0 = pos.top;
    ir.W0 = sz.width;
    ir.H0 = sz.height;

    const onMove = (ev) => {
      if (ir.mode === 'move') {
        const dx = ev.clientX - ir.startX;
        const dy = ev.clientY - ir.startY;
        const o = clampPanel(ir.L0 + dx, ir.T0 + dy, ir.W0, ir.H0);
        setPanelPosition({ left: o.left, top: o.top });
        return;
      }
      if (ir.mode === 'resize' && ir.edge) {
        const dx = ev.clientX - ir.startX;
        const dy = ev.clientY - ir.startY;
        const o = applyResizeByEdge(ir.edge, dx, dy, ir.L0, ir.T0, ir.W0, ir.H0);
        setPanelPosition({ left: o.left, top: o.top });
        setPanelSize({ width: o.width, height: o.height });
      }
    };

    const onUp = () => {
      ir.mode = 'idle';
      ir.edge = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, []);

  const onHeaderPointerDown = useCallback(
    (e) => {
      if (e.button !== 0) return;
      if (e.target.closest('button')) return;
      e.preventDefault();
      startPanelInteraction('move', null, e.clientX, e.clientY);
    },
    [startPanelInteraction]
  );

  const onResizeHandlePointerDown = useCallback(
    (edge) => (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      startPanelInteraction('resize', edge, e.clientX, e.clientY);
    },
    [startPanelInteraction]
  );

  if (!isAuthenticated || skipPublicPaths) return null;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={onPickImage}
      />

      {!open && (
        <Tooltip title="Revive Pay Note & Todos">
          <Fab
            size="medium"
            aria-label="Open quick notes"
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              e.preventDefault();
              const d = fabDragRef.current;
              d.dragging = false;
              d.startX = e.clientX;
              d.startY = e.clientY;
              d.startRight = fabPosition.right;
              d.startBottom = fabPosition.bottom;
              const onMove = (me) => {
                const dx = me.clientX - d.startX;
                const dy = me.clientY - d.startY;
                if (!d.dragging && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
                d.dragging = true;
                const vw = window.innerWidth;
                const vh = window.innerHeight;
                const newRight = Math.max(8, Math.min(vw - 56, d.startRight - dx));
                const newBottom = Math.max(8, Math.min(vh - 56, d.startBottom - dy));
                setFabPosition({ right: newRight, bottom: newBottom });
              };
              const onUp = () => {
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
              };
              window.addEventListener('mousemove', onMove);
              window.addEventListener('mouseup', onUp);
            }}
            onClick={() => {
              if (fabDragRef.current.dragging) return;
              setOpen(true);
            }}
            sx={{
              position: 'fixed',
              zIndex: (theme) => theme.zIndex.drawer + 2,
              right: fabPosition.right,
              bottom: fabPosition.bottom,
              cursor: 'grab',
              boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
              bgcolor: sticky.fabBg,
              color: sticky.fabColor,
              '&:hover': { bgcolor: '#ffc107' },
              '&:active': { cursor: 'grabbing' },
            }}
          >
            <StickyNote2OutlinedIcon />
          </Fab>
        </Tooltip>
      )}

      {open && (
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            zIndex: (theme) => theme.zIndex.modal - 1,
            left: panelPosition.left,
            top: panelPosition.top,
            width: panelSize.width,
            height: panelSize.height,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: '10px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
            border: '1px solid rgba(0,0,0,0.12)',
          }}
        >
          {/* Header: list (+ · title · … · ×) · edit (← · title · … · ×) */}
          <Box
            onPointerDown={onHeaderPointerDown}
            sx={{
              position: 'relative',
              flexShrink: 0,
              height: 44,
              px: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              bgcolor: sticky.headerBg,
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
              userSelect: 'none',
            }}
          >
            {screen === 'edit' ? (
              <>
                <Tooltip title="All notes">
                  <IconButton
                    size="small"
                    onClick={() => {
                      void handleBackToList();
                    }}
                    sx={{ color: sticky.headerIcon }}
                    aria-label="Back to list"
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="New note">
                  <IconButton
                    size="small"
                    onClick={() => void handleNewNote()}
                    sx={{ color: sticky.headerIcon }}
                    aria-label="New note"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="New note">
                <IconButton size="small" onClick={() => void handleNewNote()} sx={{ color: sticky.headerIcon }} aria-label="New note">
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Typography
              variant="subtitle2"
              sx={{
                flex: 1,
                textAlign: 'center',
                fontWeight: 700,
                color: sticky.headerIcon,
                letterSpacing: 0.2,
                userSelect: 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {screen === 'edit' ? (activeNoteId ? 'Note' : 'New note') : 'Revive Pay Note & Todos'}
            </Typography>
            <Button
              size="small"
              variant="text"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => setNoteType(noteType === 'todo' ? 'note' : 'todo')}
              sx={{
                flexShrink: 0,
                minWidth: 0,
                px: 1,
                py: 0.25,
                fontWeight: 700,
                fontSize: '0.8125rem',
                textTransform: 'none',
                color: sticky.headerIcon,
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
              }}
              aria-label={noteType === 'todo' ? 'Switch to notes' : 'Switch to todos'}
            >
              {noteType === 'todo' ? 'Notes' : 'Todo'}
            </Button>
            <Tooltip title="Close">
              <IconButton
                size="small"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => void handleClose()}
                sx={{ color: sticky.headerIcon }}
                aria-label="Close"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {saving && (
              <LinearProgress
                variant="indeterminate"
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 3,
                  borderRadius: 0,
                  bgcolor: 'rgba(0,0,0,0.1)',
                  '& .MuiLinearProgress-bar': { bgcolor: '#2e7d32' },
                }}
              />
            )}
            {saveShowSuccess && !saving && (
              <Box
                aria-live="polite"
                sx={{
                  position: 'absolute',
                  right: 88,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  pointerEvents: 'none',
                  animation: 'quickNotesFade 2.2s ease-out forwards',
                  '@keyframes quickNotesFade': {
                    '0%': { opacity: 1 },
                    '70%': { opacity: 1 },
                    '100%': { opacity: 0 },
                  },
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 22, color: '#2e7d32' }} />
              </Box>
            )}
          </Box>

          {screen === 'list' && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: noteType === 'todo' ? todoUi.pageBg : '#1a1a1a',
                position: 'relative',
              }}
            >
              <TextField
                size="small"
                fullWidth
                placeholder={noteType === 'todo' ? 'Search todos...' : 'Search notes...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  mx: 1.5,
                  mt: 1,
                  mb: noteType === 'todo' ? 0.75 : 0.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: noteType === 'todo' ? '#252525' : '#2d2d2d',
                    color: sticky.bodyText,
                    transition: 'box-shadow 0.18s ease, border-color 0.18s ease',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 3px rgba(253,216,53,0.12)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(253,216,53,0.55)',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-input::placeholder': { color: sticky.placeholder, opacity: 1 },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon sx={{ color: 'rgba(255,255,255,0.42)', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              {noteType === 'todo' ? (
                <>
                  <Box sx={{ display: 'flex', gap: 1, px: 1.5, mb: 1, alignItems: 'stretch' }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="What needs to be done?"
                      value={todoNewInput}
                      onChange={(e) => setTodoNewInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void handleQuickAddTodo();
                        }
                      }}
                      disabled={todoQuickAddBusy}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: todoUi.inputBg,
                          color: todoUi.inputText,
                          transition: 'box-shadow 0.18s ease, border-color 0.18s ease',
                          '& fieldset': { borderColor: 'rgba(0,0,0,0.14)' },
                          '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.24)' },
                          '&.Mui-focused': {
                            boxShadow: '0 0 0 3px rgba(25,118,210,0.18)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(25,118,210,0.65)',
                            borderWidth: '1px',
                          },
                        },
                        '& .MuiOutlinedInput-input::placeholder': { color: 'rgba(0,0,0,0.45)', opacity: 1 },
                      }}
                    />
                    <Button
                      variant="contained"
                      disabled={todoQuickAddBusy || !todoNewInput.trim()}
                      onClick={() => void handleQuickAddTodo()}
                      sx={{
                        alignSelf: 'stretch',
                        bgcolor: todoUi.addBtn,
                        color: todoUi.addBtnText,
                        fontWeight: 700,
                        borderRadius: 2,
                        minWidth: 76,
                        px: 2,
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: '#1565c0', boxShadow: 'none' },
                      }}
                    >
                      Add
                    </Button>
                  </Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      textAlign: 'center',
                      fontWeight: 700,
                      color: '#fff',
                      mb: 1,
                      letterSpacing: 0.3,
                    }}
                  >
                    Task List
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" sx={{ px: 2, mb: 0.5, color: sticky.placeholder }}>
                  Notes
                  {Number.isFinite(total) && total > 0 ? ` · ${total} saved` : ''}
                  {hasMoreOlder ? ' · scroll up for older' : ''}
                </Typography>
              )}
              <Box
                ref={historyScrollRef}
                onScroll={onHistoryScroll}
                sx={{
                  ...quickNotesListScrollSx,
                }}
              >
                {loadingOlder && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={18} sx={{ color: sticky.placeholder }} />
                  </Box>
                )}
                {loadingInitial ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} sx={{ color: sticky.placeholder }} />
                  </Box>
                ) : filteredNotes.length === 0 ? (
                  <Typography variant="body2" sx={{ color: sticky.placeholder, py: 2, px: 0.5 }}>
                    {items.length === 0
                      ? noteType === 'todo'
                        ? 'No todos yet. Add one above.'
                        : 'No notes yet. Tap + to add one — text stays on screen while it saves.'
                      : noteType === 'todo'
                        ? 'No todos match your search.'
                        : 'No notes match your search.'}
                  </Typography>
                ) : noteType === 'todo' ? (
                  filteredNotes.map((row, idx) => {
                    const raw = pickNoteText(row);
                    const display = raw || '—';
                    const done = isTodoComplete(row);
                    const busy = noteActionBusyId === row.id;
                    return (
                      <Box
                        key={row.id ?? `todo-${idx}`}
                        sx={{
                          mb: 1,
                          borderRadius: 1,
                          overflow: 'hidden',
                          bgcolor: '#353535',
                          border: '1px solid rgba(255,255,255,0.08)',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                          '&:hover': {
                            borderColor: 'rgba(253,216,53,0.45)',
                            bgcolor: '#3d3d3d',
                          },
                        }}
                      >
                        <Box sx={{ height: 3, bgcolor: sticky.headerBg }} />
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            py: 0.5,
                            minHeight: 40,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={done}
                            disabled={busy}
                            onChange={() => void handleToggleTodoComplete(row)}
                            sx={{
                              p: 0.25,
                              ml: -0.5,
                              flexShrink: 0,
                              color: '#fff',
                              '&.Mui-checked': { color: todoUi.checkColor },
                            }}
                          />
                          <Box
                            onClick={() => void openNoteForEdit(row)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                void openNoteForEdit(row);
                              }
                            }}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              cursor: 'pointer',
                              borderRadius: 0.5,
                              outline: 'none',
                              overflow: 'hidden',
                              '&:focus-visible': { boxShadow: `0 0 0 2px ${sticky.headerBg}` },
                            }}
                          >
                            <Typography
                              component="div"
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                lineHeight: 1.45,
                                color: done ? todoUi.textDone : sticky.bodyText,
                                textDecoration: done ? 'line-through' : 'none',
                              }}
                            >
                              {highlightPreviewSpans(display, searchQuery).map(({ key, text, mark }) =>
                                mark ? (
                                  <Box
                                    component="span"
                                    key={key}
                                    sx={{ bgcolor: sticky.headerBg, color: '#1a1a1a', borderRadius: 0.25, px: 0.125 }}
                                  >
                                    {text}
                                  </Box>
                                ) : (
                                  <span key={key}>{text}</span>
                                )
                              )}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0 }}>
                            <Tooltip title="Delete">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={busy}
                                  onClick={(e) => void handleDeleteNote(row, e)}
                                  sx={{ color: '#fff', p: 0.375 }}
                                  aria-label="Delete todo"
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={busy}
                                  onClick={() => void openNoteForEdit(row)}
                                  sx={{ color: '#fff', p: 0.375 }}
                                  aria-label="Edit todo"
                                >
                                  <EditOutlinedIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Box
                              sx={{
                                textAlign: 'right',
                                ml: 0.25,
                                pl: 0.75,
                                borderLeft: '1px solid rgba(255,255,255,0.12)',
                                lineHeight: 1.2,
                                flexShrink: 0,
                                maxWidth: 132,
                              }}
                            >
                              <Typography variant="caption" sx={{ whiteSpace: 'nowrap', display: 'block' }}>
                                <Box component="span" sx={{ color: 'rgba(253,216,53,0.85)', fontWeight: 700 }}>
                                  {formatCardDate(row)}
                                </Box>
                                {formatCardTime(row) ? (
                                  <Box component="span" sx={{ color: 'rgba(253,216,53,0.55)', fontWeight: 500, ml: 0.5 }}>
                                    · {formatCardTime(row)}
                                  </Box>
                                ) : null}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  filteredNotes.map((row, idx) => {
                    const line = previewLine(row);
                    const busy = noteActionBusyId === row.id;
                    return (
                      <Box
                        key={row.id ?? `row-${idx}`}
                        sx={{
                          mb: 1,
                          borderRadius: 1,
                          overflow: 'hidden',
                          bgcolor: '#353535',
                          border: '1px solid rgba(255,255,255,0.08)',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                          '&:hover': {
                            borderColor: 'rgba(253,216,53,0.45)',
                            bgcolor: '#3d3d3d',
                          },
                        }}
                      >
                        <Box sx={{ height: 3, bgcolor: sticky.headerBg }} />
                        <Box sx={{ p: 1.25, pt: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 0.5,
                              mb: 0.5,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 28, minWidth: 0 }}>
                              {isPinned(row) ? (
                                <PushPinIcon
                                  sx={{ fontSize: 18, color: sticky.headerBg, flexShrink: 0 }}
                                  aria-hidden
                                />
                              ) : null}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0, pt: 0.125 }}>
                              <Tooltip title={isPinned(row) ? 'Unpin' : 'Pin to top'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={busy}
                                    onClick={(e) => void handleTogglePin(row, e)}
                                    sx={{ color: '#fff', p: 0.375 }}
                                    aria-label={isPinned(row) ? 'Unpin note' : 'Pin note'}
                                  >
                                    {isPinned(row) ? (
                                      <PushPinIcon sx={{ fontSize: 20 }} />
                                    ) : (
                                      <PushPinOutlinedIcon sx={{ fontSize: 20 }} />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={busy}
                                    onClick={(e) => void handleDeleteNote(row, e)}
                                    sx={{ color: '#fff', p: 0.375 }}
                                    aria-label="Delete note"
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Box
                                sx={{
                                  textAlign: 'right',
                                  ml: 0.5,
                                  pl: 0.5,
                                  lineHeight: 1.15,
                                  minWidth: 0,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'rgba(253,216,53,0.85)',
                                    fontWeight: 600,
                                    display: 'block',
                                  }}
                                >
                                  {formatCardDate(row)}
                                </Typography>
                                {formatCardTime(row) ? (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'rgba(253,216,53,0.58)',
                                      fontWeight: 500,
                                      display: 'block',
                                      fontSize: '0.68rem',
                                    }}
                                  >
                                    {formatCardTime(row)}
                                  </Typography>
                                ) : null}
                              </Box>
                            </Box>
                          </Box>
                          <Box
                            onClick={() => void openNoteForEdit(row)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                void openNoteForEdit(row);
                              }
                            }}
                            sx={{
                              cursor: 'pointer',
                              borderRadius: 0.5,
                              outline: 'none',
                              '&:focus-visible': { boxShadow: `0 0 0 2px ${sticky.headerBg}` },
                            }}
                          >
                            <Typography
                              component="div"
                              variant="body2"
                              sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                lineHeight: 1.45,
                                color: sticky.bodyText,
                              }}
                            >
                              {highlightPreviewSpans(line, searchQuery).map(({ key, text, mark }) =>
                                mark ? (
                                  <Box
                                    component="span"
                                    key={key}
                                    sx={{ bgcolor: sticky.headerBg, color: '#1a1a1a', borderRadius: 0.25, px: 0.125 }}
                                  >
                                    {text}
                                  </Box>
                                ) : (
                                  <span key={key}>{text}</span>
                                )
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
              {openingNoteId != null && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 2,
                    bgcolor: 'rgba(0,0,0,0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress sx={{ color: sticky.headerBg }} />
                </Box>
              )}
            </Box>
          )}

          {screen === 'edit' && (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Box
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                data-placeholder={noteType === 'todo' ? 'Edit todo…' : 'Take a note...'}
                onInput={onEditorInput}
                onBlur={onEditorBlur}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  px: 2,
                  py: 1.5,
                  bgcolor: sticky.bodyBg,
                  color: sticky.bodyText,
                  outline: 'none',
                  fontSize: '0.95rem',
                  lineHeight: 1.5,
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                  '&:empty:before': {
                    content: 'attr(data-placeholder)',
                    color: sticky.placeholder,
                    pointerEvents: 'none',
                  },
                  '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                }}
              />
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  px: 1,
                  py: 0.75,
                  bgcolor: sticky.footerBg,
                  borderTop: `1px solid ${sticky.footerBorder}`,
                }}
              >
                {activeNoteId && activeListRow?.id === activeNoteId && (
                  <>
                    {noteType !== 'todo' && (
                      <Tooltip title={isPinned(activeListRow) ? 'Unpin' : 'Pin to top'}>
                        <span>
                          <IconButton
                            size="small"
                            onMouseDown={(e) => e.preventDefault()}
                            disabled={noteActionBusyId === activeNoteId}
                            onClick={() => void handleTogglePin(activeListRow)}
                            sx={{ color: sticky.bodyText }}
                            aria-label={isPinned(activeListRow) ? 'Unpin note' : 'Pin note'}
                          >
                            {isPinned(activeListRow) ? (
                              <PushPinIcon sx={{ fontSize: 20 }} />
                            ) : (
                              <PushPinOutlinedIcon sx={{ fontSize: 20 }} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          size="small"
                          onMouseDown={(e) => e.preventDefault()}
                          disabled={noteActionBusyId === activeNoteId}
                          onClick={() => void handleDeleteNote(activeListRow)}
                          sx={{ color: sticky.bodyText }}
                          aria-label={noteType === 'todo' ? 'Delete todo' : 'Delete note'}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Box
                      sx={{
                        width: 1,
                        height: 22,
                        bgcolor: 'rgba(255,255,255,0.12)',
                        mx: 0.35,
                        alignSelf: 'center',
                      }}
                    />
                  </>
                )}
                <IconButton
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applyFormat('bold')}
                  sx={{ color: sticky.bodyText }}
                >
                  <FormatBoldIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applyFormat('italic')}
                  sx={{ color: sticky.bodyText }}
                >
                  <FormatItalicIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applyFormat('underline')}
                  sx={{ color: sticky.bodyText }}
                >
                  <FormatUnderlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applyFormat('strikeThrough')}
                  sx={{ color: sticky.bodyText }}
                >
                  <StrikethroughSIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applyFormat('insertUnorderedList')}
                  sx={{ color: sticky.bodyText }}
                >
                  <FormatListBulletedIcon sx={{ fontSize: 20 }} />
                </IconButton>
                {noteType === 'note' && (
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ color: sticky.bodyText }}
                    aria-label="Attach image"
                  >
                    <ImageOutlinedIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}

          {[
            { edge: 'n', cursor: 'ns-resize', sx: { top: RESIZE_TOP, left: CORNER_HIT, right: CORNER_HIT, height: EDGE_HIT } },
            { edge: 's', cursor: 'ns-resize', sx: { bottom: 0, left: CORNER_HIT, right: CORNER_HIT, height: EDGE_HIT } },
            { edge: 'e', cursor: 'ew-resize', sx: { top: RESIZE_TOP, bottom: 0, right: 0, width: EDGE_HIT } },
            { edge: 'w', cursor: 'ew-resize', sx: { top: RESIZE_TOP, bottom: 0, left: 0, width: EDGE_HIT } },
            { edge: 'nw', cursor: 'nwse-resize', sx: { top: RESIZE_TOP, left: 0, width: CORNER_HIT, height: CORNER_HIT } },
            { edge: 'ne', cursor: 'nesw-resize', sx: { top: RESIZE_TOP, right: 0, width: CORNER_HIT, height: CORNER_HIT } },
            { edge: 'sw', cursor: 'nesw-resize', sx: { bottom: 0, left: 0, width: CORNER_HIT, height: CORNER_HIT } },
            { edge: 'se', cursor: 'nwse-resize', sx: { bottom: 0, right: 0, width: CORNER_HIT, height: CORNER_HIT } },
          ].map(({ edge, cursor, sx: hitSx }) => (
            <Box
              key={edge}
              aria-hidden={edge === 'se' ? undefined : true}
              aria-label={edge === 'se' ? 'Resize panel' : undefined}
              role={edge === 'se' ? 'separator' : undefined}
              onPointerDown={onResizeHandlePointerDown(edge)}
              sx={{
                position: 'absolute',
                zIndex: 6,
                touchAction: 'none',
                cursor,
                ...hitSx,
                display: 'flex',
                alignItems: edge === 'se' ? 'flex-end' : 'stretch',
                justifyContent: edge === 'se' ? 'flex-end' : 'stretch',
              }}
            >
              {edge === 'se' && (
                <DragIndicatorIcon
                  sx={{
                    pointerEvents: 'none',
                    transform: 'rotate(-45deg)',
                    fontSize: 22,
                    color: 'rgba(255,255,255,0.35)',
                    pr: 0.25,
                    pb: 0.25,
                  }}
                />
              )}
            </Box>
          ))}
        </Paper>
      )}
    </>
  );
};

export default QuickNotesDock;
